import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const TrackingTimeline = ({ stages, currentStage }) => {
  const getStageIcon = (stageName) => {
    switch (stageName) {
      case 'Package Received':
        return 'PackageCheck';
      case 'In Transit':
        return 'Truck';
      case 'Out for Delivery':
        return 'MapPin';
      case 'Delivered':
        return 'CheckCircle2';
      default:
        return 'Package';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-warm p-6">
      <h2 className="text-xl font-heading font-bold text-foreground mb-6">
        Shipment Progress
      </h2>
      {/* Desktop Timeline - Horizontal */}
      <div className="hidden md:block">
        <div className="relative">
          {/* Progress Line */}
          <div className="absolute top-6 left-0 right-0 h-1 bg-muted">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${((currentStage - 1) / (stages?.length - 1)) * 100}%` }}
              transition={{ duration: 1, ease: 'easeOut' }}
              className="h-full bg-primary"
            />
          </div>

          {/* Stages */}
          <div className="relative flex justify-between">
            {stages?.map((stage, index) => (
              <motion.div
                key={stage?.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex flex-col items-center flex-1"
              >
                {/* Icon Circle */}
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center border-4 border-background z-10 transition-all duration-300 ${
                    stage?.completed
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  <Icon name={getStageIcon(stage?.name)} size={20} />
                </div>

                {/* Stage Info */}
                <div className="mt-4 text-center max-w-[120px]">
                  <p
                    className={`text-sm font-body font-semibold mb-1 ${
                      stage?.completed ? 'text-foreground' : 'text-muted-foreground'
                    }`}
                  >
                    {stage?.name}
                  </p>
                  {stage?.completed && stage?.date && (
                    <>
                      <p className="text-xs font-body text-muted-foreground">{stage?.date}</p>
                      {stage?.location && (
                        <p className="text-xs font-body text-muted-foreground mt-1">{stage?.location}</p>
                      )}
                    </>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
      {/* Mobile Timeline - Vertical */}
      <div className="md:hidden space-y-4">
        {stages?.map((stage, index) => (
          <motion.div
            key={stage?.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="flex gap-4"
          >
            {/* Icon and Line */}
            <div className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center border-4 border-background flex-shrink-0 transition-all duration-300 ${
                  stage?.completed
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                <Icon name={getStageIcon(stage?.name)} size={18} />
              </div>
              {index < stages?.length - 1 && (
                <div className="w-1 flex-1 mt-2 mb-2 bg-muted relative overflow-hidden">
                  {stage?.completed && (
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: '100%' }}
                      transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
                      className="w-full bg-primary"
                    />
                  )}
                </div>
              )}
            </div>

            {/* Stage Info */}
            <div className="flex-1 pb-6">
              <p
                className={`text-base font-body font-semibold mb-1 ${
                  stage?.completed ? 'text-foreground' : 'text-muted-foreground'
                }`}
              >
                {stage?.name}
              </p>
              {stage?.completed && stage?.date && (
                <>
                  <p className="text-sm font-body text-muted-foreground">{stage?.date}</p>
                  {stage?.location && (
                    <p className="text-sm font-body text-muted-foreground mt-1 flex items-center gap-1">
                      <Icon name="MapPin" size={14} />
                      {stage?.location}
                    </p>
                  )}
                </>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default TrackingTimeline;